<template>
  <div
    ref="popupContainerRef"
    class="popup-container improve-slide-search"
    v-click-outside="vcoConfig"
  >
    <div class="main-content">
      <div class="slides-container">
        <QuizGenerator
          @handleDiveInQuiz="handleDiveInQuiz"
          :isShowQuizGeneratorContent="isShowQuizGeneratorContent"
          v-if="isAITestGroup"
        />
        <SlideSearch
          v-if="!isEURegion && !isAITestGroup"
          @search="handleSearch"
        />
        <SlideTabs
          v-if="isSlideTabsVisible"
          :allTypeSections="allTypeSections"
          @handleHoverOnSlideType="handleHoverOnSlideType"
          @handleMouseLeaveSlideType="handleMouseLeaveSlideType"
          @handleSelectSlideType="handleSelectSlideType"
        />

        <ImportSheet v-if="isImportSheetVisible" />

        <div
          v-show="isShowPreview && !$store.state.isTouchScreen"
          id="slide-type-popup-preview"
          ref="previewRef"
          class="preview"
        >
          <div class="preview-content">
            <CanvasPreview
              id="slide-search-popup"
              :defaultSlide="allTypeSections[0].slideTypes[0]"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import SelectSlideTypeMixin from '@/mixins/slide/SelectSlideTypeMixin'
import UndoRedoMixin from '@/mixins/undo-redo/UndoRedoMixin'
import SlideSearch from './SlideSearch.vue'
import SlideTabs from './SlideTabs.vue'
import QuizGenerator from './OpenAI/QuizGenerator.vue'
import CanvasPreview from '@/components-v2/common/canvas-preview/CanvasPreview.vue'

import allSlideTypes from '@/constant/allSlideTypes'
import EventBus from '@/eventBus'
import ImportSheet from '@/components-v2/presentation-editor/import-sheet/ImportSheet.vue'
import configs from '@/config'
import constants from '@/constant'

const previewPickImage = require('@/assets/images/slide-type-preview/pick-image.svg')

export default {
  components: {
    SlideSearch,
    SlideTabs,
    QuizGenerator,
    CanvasPreview,
    ImportSheet,
  },
  mixins: [UndoRedoMixin, SelectSlideTypeMixin],
  props: {
    closeSlideSearchPopup: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      allTypeSections: [],
      isShowQuizGeneratorContent: false,
      isShowPreview: false,
      resizeObserver: null,
      hasTextSearch: false,
      vcoConfig: {
        handler: this.handler,
        middleware: this.middleware,
      },
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
    }),
    ...mapGetters(['isAITestGroup']),
    isEURegion() {
      return configs.region === constants.euRegion
    },
    isSlideTabsVisible() {
      return (this.isAITestGroup && !this.isShowQuizGeneratorContent) ||
      (!this.isAITestGroup && !this.hasTextSearch)
    },
    isImportSheetVisible() {
      return (!this.isShowQuizGeneratorContent && this.isAITestGroup) || !this.isAITestGroup
    },
    isAbTestMergeQuiz() {
      return this.currentUser && this.currentUser.id % 4 === 3
    },
  },
  created() {
    this.allTypeSections = this.applyMergeQuizForAbTest([...allSlideTypes])
    EventBus.$on('slide-search-popup:close', this.closeSlideSearchPopup)
  },
  mounted() {
    if (this.$store.state.createdPresentationFromQuizHintsBanner) {
      const payload = { // create a quiz slide
        item: {
          name: 'Pick Answer',
          icon: 'fas fa-tasks',
          type: 'pickAnswer',
          slideType: null,
          desc: 'Ask a quiz question and write the options. Participants try to choose the correct answer to score points.',
          label: 'Quiz',
          ahaIcon: 'pick-answer',
          ahaIconWhite: 'pick-answer-w',
          preview: 'pickAnswer',
          previewUrl: '/723e47a009d5db8436363ef227c6577a.svg',
          videoTutorialUrl: 'https://youtu.be/RGX2dmuvTEA',
          error: false,
          isLoading: false,
        },
        property: 'quiz-hints-banner',
      }
      this.handleSelectSlideType(payload)
    }
    this.handlePreviewSize()
    this.registerHistory('SideLeft')
  },
  destroyed() {
    EventBus.$off('slide-search-popup:close', this.closeSlideSearchPopup)
    if (!this.resizeObserver) return
    this.resizeObserver.unobserve(document.body)
  },
  methods: {
    applyMergeQuizForAbTest(rawAllSlideTypes) {
      if (!this.isAbTestMergeQuiz) {
        return rawAllSlideTypes
      }
      const newAllSlideTypes = JSON.parse(JSON.stringify(rawAllSlideTypes))
      const allSlidesTypePopular = rawAllSlideTypes[0].slideTypes
      allSlidesTypePopular[1] = {
        name: 'Pick Image',
        icon: 'fas fa-images',
        type: 'pickAnswer',
        slideType: 'imageChoice',
        desc: 'Ask a quiz question and provide image options. Participants try to choose the correct image to score points.',
        label: 'Quiz',
        ahaIcon: 'pick-image',
        ahaIconWhite: 'pick-image-w',
        preview: 'pickImage',
        previewUrl: previewPickImage,
      }
      newAllSlideTypes[0].slideTypes = allSlidesTypePopular
      return JSON.parse(JSON.stringify(newAllSlideTypes))
    },
    handlePreviewSize() {
      this.$nextTick(function _() {
        const maxWidth = 676
        const maxHeight = 575

        this.resizeObserver = new ResizeObserver((entries) => {
          if (this.$refs.previewRef && this.$refs.popupContainerRef) {
            const { offsetWidth } = entries[0].target
            const {
              top,
              right,
              bottom,
            } = this.$refs.popupContainerRef.getBoundingClientRect()
            const bounding = {
              top: top + 165,
              right: offsetWidth - 20,
              bottom,
              left: right + 20,
            }

            const maxPreviewWidth = bounding.right - bounding.left < maxWidth
              ? bounding.right - bounding.left
              : maxWidth

            const maxPreviewHeight = bounding.bottom - bounding.top < maxHeight
              ? bounding.bottom - bounding.top
              : maxHeight

            let w
            let h

            if ((maxPreviewWidth * maxHeight) / maxWidth > maxPreviewHeight) {
              h = maxPreviewHeight
              w = (h * maxWidth) / maxHeight
            } else {
              w = maxPreviewWidth
              h = (w * maxHeight) / maxWidth
            }

            this.$refs.previewRef.style.width = `${w}px`
            this.$refs.previewRef.style.height = `${h}px`
          }
        })

        this.resizeObserver.observe(document.body)
      })
    },
    handleSelectSlideType(payload) {
      this.selectSlideType(payload) // mixin SelectSlideType
      this.closeSlideSearchPopup()
    },
    handleHoverOnSlideType(item, mode) {
      if (this.$store.state.isTouchScreen) return

      this.isShowPreview = true
      EventBus.$emit('handleHoverOnSlideType', item, mode, 'slide-search-popup')
    },
    handleMouseLeaveSlideType() {
      this.isShowPreview = false
    },
    handleDiveInQuiz(status = true) {
      this.isShowQuizGeneratorContent = status
    },
    handler() {
      this.closeSlideSearchPopup()
    },
    middleware(event) {
      return !event.target.closest('.new-slide-context-cta') &&
        !event.target.closest('.slides-container') &&
        !event.target.closest('.aha-modal__content')
    },
    handleSearch(textSearch) {
      this.hasTextSearch = !!textSearch
    },

  },
}
</script>

<style lang="scss" scoped>
.popup-container {
  position: relative;
  display: flex;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);
  width: 500px;
  height: calc(100% - 36px);
  margin-top: 16px;
  margin-left: 20px;
}

.main-content {
  height: 100%;
  width: 100%;
}

.slides-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.preview {
  position: absolute;
  max-width: 676px;
  max-height: 575px;
  top: 165px;
  left: calc(100% + 20px);
  box-shadow: 0px 4px 27px rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  aspect-ratio: 676 / 578 !important;
}
</style>
